import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "./api";
import { track } from "../analytics";
import { detectDeviceKind } from "./playCollection";
import {
  DEFAULT_SCREEN_SETTINGS,
  type ScreenSettings,
  readScreenSettings,
  rekeyScreenSettings,
  screenKey,
  writeScreenSettings,
} from "./screenSettings";
import type { ScreenOrigin } from "./screenOrigin";

/**
 * This tab's claimable-screen identity: announce once, poll for the claim, and when a
 * controller redeems the code, BECOME the screen it claimed.
 *
 * Extracted from PendingScreen because the code is not a /screen thing any more — the
 * site bar shows "Code: XXXX" on every page, so any tab of this site can be turned
 * into a screen by whoever can read its code. The trust model is unchanged from the TV
 * flow it generalises: the code never leaves this tab except by being physically read off
 * it, and redeeming one only redirects THIS tab — it grants nothing else.
 *
 * TWO KINDS OF CODE now ride this one hook, split by `adopt`:
 *
 *   AMBIENT (no `adopt`) — every non-screen page, and the pending screen. Redeeming the
 *   code MINTS a device and this tab navigates to it. The server rotates the code on its
 *   TTL (a lapsed entry 404s the poll and the hook re-announces).
 *
 *   ADOPT (`adopt` = the device this tab already is) — a claimed screen. Redeeming the
 *   code moves THAT device to the redeemer in place: ownership changes, nothing is
 *   minted, and this tab stays exactly where it is. The server never answers an adopt
 *   entry's poll with a deviceId, so the navigate branch below simply never fires. Each
 *   poll renews the entry, so the code never rotates while the tab lives — and dies on
 *   the TTL once the tab closes and the polling stops.
 *
 * ONE IDENTITY PER (TAB, TARGET), cached at module level. The bar unmounts and remounts
 * as the router moves between the landing, the control pages and a screen, and a hook
 * that announced per mount would show a different code after every navigation —
 * obsoleting the one somebody across the room is halfway through typing. The cache is a
 * map keyed by adopt target (the ambient identity under "") rather than a single slot,
 * because one navigation can walk a tab from ambient chrome onto a claimed screen —
 * reusing the ambient entry there would hand out a code that mints when it must adopt.
 *
 * The ANNOUNCE IS CACHED AS A PROMISE, not a flag, for the reason MintedScreen records:
 * StrictMode double-invokes effects in dev, and with a flag the second run skips the work
 * while the first run's result is discarded by its own liveness check. Both runs attach
 * to one promise instead.
 *
 * ON CLAIM (ambient only) it navigates to `/view/d/:deviceId` — the device the claim just
 * minted — with `replace`, so Back leaves the screen rather than re-entering the page
 * that got claimed. (Not `/screen`: that page announces a NEW pending screen, which would
 * drop the claim on the floor.)
 *
 * THE IDENTITY IS RE-KEYED RATHER THAN FORGOTTEN, and that is what stops the code on the
 * glass from changing under whoever just typed it. The server converts a claimed pending
 * entry into an adopt entry for the device it created (see PendingScreenRegistry.claim),
 * so the same screenId and the same four characters are now the claimed screen's adopt
 * code. Moving the cache entry from the ambient key to the device key hands it straight to
 * the `usePairing(deviceId)` that mounts a moment later on /view/d/:id. Forgetting it
 * instead — which is what this did — made that hook announce a fresh entry, and the code
 * somebody had just read off the television was replaced by a different one. It looks
 * exactly like a failed pairing, and the reasonable response to it is to type again.
 */

/**
 * What a screen needs to wear a party badge and to say who is holding it.
 *
 * Every action is a function rather than something the badge reaches for, so the badge and
 * the settings dialog both render standalone in a check with no socket and no router —
 * the same rule ScreenControls is built on.
 */
export interface PartyState {
  settings: ScreenSettings;
  onChange: (next: ScreenSettings) => void;
  /** The address to put in the QR, or null until this screen has a code to encode. */
  url: string | null;
  /** A LOCAL deadline for the hold, or null when this screen is free to claim. */
  heldUntil: number | null;
  /** Let go of a running hold now — the screen's own override, and the only one. */
  onRelease: () => void;
}

interface PendingIdentity {
  screenId: string;
  /** Mutable: the poll refreshes it when the server renews a lapsing entry, so a bar
   *  remounting mid-session paints the CURRENT code rather than the announce-time one. */
  code: string;
  /**
   * A hold on this screen, as a LOCAL deadline. Mutable for the same reason `code` is.
   *
   * A deadline rather than the duration the wire carries, because this outlives the
   * response it came from: a duration parked on a cached identity is a number that
   * silently ages into a lie, while a deadline stays true however long it sits here.
   */
  heldUntil: number | null;
}

const identities = new Map<string, PendingIdentity>();
const announces = new Map<string, Promise<PendingIdentity>>();
const keyOf = (adopt?: string): string => adopt ?? "";

/*
 * THE CACHE OUTLIVES A RELOAD, in sessionStorage — per TAB, exactly the scope the cache
 * above already claims ("one identity per (tab, target)"). 2026-10-06 QA: F5 on /screen
 * put a different four characters on the glass, because the module-level map died with
 * the page and the hook announced a fresh entry while the old one stayed redeemable for
 * PENDING_TTL_MS (the server's "two working codes" note in pending-screens.ts). Now the
 * reloaded tab polls the entry it already had; a 404 (lapsed, server restart) still
 * re-announces through the same path as before. Read lazily from the announce — never
 * at render — so a static render touches no storage.
 */
const STORE_KEY = "gl.pendingScreens";
let hydrated = false;
/*
 * ...BUT sessionStorage IS NOT QUITE PER TAB. A tab opened FROM this one (window.open, a
 * duplicated tab) starts with a COPY of it, so the copy hydrated the same identity and
 * two screens wore one code — one entry on the server, polled twice, and a claim that
 * could only ever mean one of them (2026-10-10 review). An identity read back from
 * storage is therefore only a CLAIM until no other tab objects: before its first use the
 * tab asks the channel below whether anyone is already live on that screen id. The tab
 * it was copied from answers, and the copy drops it and announces its own. A plain
 * reload has nobody to answer — the page that held it is gone — and keeps its code,
 * which is the whole point of the cache. No BroadcastChannel: as before, trust storage.
 */
const inherited = new Set<string>();
const HELD_ELSEWHERE_WAIT_MS = 150;
type HoldMessage = { t: "who" | "mine"; screenId: string };
let channel: BroadcastChannel | null = null;
const openChannel = (): void => {
  if (channel || typeof BroadcastChannel === "undefined") return;
  try {
    channel = new BroadcastChannel(STORE_KEY);
    channel.addEventListener("message", (e: MessageEvent<HoldMessage>) => {
      if (e.data?.t !== "who") return;
      // Only an identity this tab is actually LIVE on answers — one it merely inherited
      // and has not yet confirmed is the question, not the answer.
      for (const [k, v] of identities) {
        if (v.screenId === e.data.screenId && !inherited.has(k)) {
          channel?.postMessage({ t: "mine", screenId: v.screenId } satisfies HoldMessage);
          return;
        }
      }
    });
  } catch {
    channel = null;
  }
};
/** Whether another open tab is already live on this pending-screen id. */
const heldElsewhere = (screenId: string): Promise<boolean> => {
  const ch = channel;
  if (!ch) return Promise.resolve(false);
  return new Promise((resolve) => {
    const done = (taken: boolean) => {
      clearTimeout(t);
      ch.removeEventListener("message", onMessage);
      resolve(taken);
    };
    const onMessage = (e: MessageEvent<HoldMessage>) => {
      if (e.data?.t === "mine" && e.data.screenId === screenId) done(true);
    };
    const t = setTimeout(() => done(false), HELD_ELSEWHERE_WAIT_MS);
    ch.addEventListener("message", onMessage);
    ch.postMessage({ t: "who", screenId } satisfies HoldMessage);
  });
};
const hydrate = (): void => {
  if (hydrated) return;
  hydrated = true;
  openChannel();
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    if (!raw) return;
    const saved = JSON.parse(raw) as Record<string, Partial<PendingIdentity>>;
    for (const [k, v] of Object.entries(saved)) {
      if (identities.has(k) || typeof v?.screenId !== "string" || typeof v.code !== "string") continue;
      identities.set(k, {
        screenId: v.screenId,
        code: v.code,
        heldUntil: typeof v.heldUntil === "number" ? v.heldUntil : null,
      });
      inherited.add(k);
    }
  } catch {
    /* Blocked or corrupt storage: behave as before, one identity per page load. */
  }
};
const persist = (): void => {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(Object.fromEntries(identities)));
  } catch {
    /* Storage blocked or full: the in-memory cache still works for this page. */
  }
};
/**
 * Keys that have minted a code at least once on this page.
 *
 * The first mint for a key is the funnel step — a code is on the glass, somebody could
 * type it. Every later mint for the same key is the hook recovering from a lapse (the
 * poll 404'd: the server restarted, or forgot an entry nobody read in time) and is
 * reported as a re-issue instead. Tracked HERE rather than in the api client, which is
 * where the other funnel events live, because only this module knows which mint is which:
 * to `api.screens.pending` they are the same call, and counting them as the same event is
 * how one television came to send 618 "codes shown" in six days.
 */
const minted = new Set<string>();

const announceOnce = (
  adopt?: string,
  viewId?: string,
  holdMs?: number,
): Promise<PendingIdentity> => {
  hydrate();
  const key = keyOf(adopt);
  const have = identities.get(key);
  if (have && !inherited.has(key)) return Promise.resolve(have);
  let promise = announces.get(key);
  if (!promise) {
    const kind = detectDeviceKind();
    const mint = (): Promise<PendingIdentity> => api.screens
      .pending(kind, adopt, viewId, holdMs)
      .then((r) => {
        const made: PendingIdentity = {
          screenId: r.screenId,
          code: r.code,
          // The announce answers with the hold too, so a tab that has just navigated onto
          // a device — which is what a claim makes every screen do — is not blind to it
          // until its first poll ten seconds later.
          heldUntil: r.heldForMs ? Date.now() + r.heldForMs : null,
        };
        identities.set(key, made);
        persist();
        // Neither the code nor the screen id is sent — the code is a live credential that
        // mints a device for whoever redeems it. `mode` says whether this code would mint
        // a device (ambient) or re-home one this tab already is (adopt).
        track(minted.has(key) ? "screen_code_reissued" : "screen_code_shown", {
          kind,
          mode: adopt ? "adopt" : "ambient",
        });
        minted.add(key);
        return made;
      });
    // An inherited identity is used only once no other tab claims it (see `inherited`).
    promise = (
      have
        ? heldElsewhere(have.screenId).then((taken) => {
            inherited.delete(key);
            if (!taken) return have;
            identities.delete(key);
            persist();
            return mint();
          })
        : mint()
    )
      .catch((err: unknown) => {
        // A failed announce must not poison every later mount with a rejected promise.
        announces.delete(key);
        throw err;
      });
    announces.set(key, promise);
  }
  return promise;
};

/** Drop a cached identity — when the server no longer knows it. */
const forget = (adopt?: string): void => {
  inherited.delete(keyOf(adopt));
  identities.delete(keyOf(adopt));
  announces.delete(keyOf(adopt));
  persist();
};

/**
 * Move the ambient identity onto the device it just became, keeping its code.
 *
 * `heldUntil` is passed IN rather than carried over, and that is not a detail: the claim
 * that produced this device is also what started the hold, so whatever the cached identity
 * knew about holds a moment ago is stale by definition — it was minted before there was
 * anything to know. The poll that discovered the claim learned the real number in the same
 * response, and this is the only chance to hand it on: the identity is re-keyed precisely
 * so the screen does NOT re-announce, so nothing downstream will ask the server again.
 */
const rekeyToDevice = (deviceId: string, heldUntil: number | null): void => {
  const held = identities.get(keyOf());
  forget();
  inherited.delete(keyOf(deviceId));
  if (held) identities.set(keyOf(deviceId), { ...held, heldUntil });
  persist();
  // The settings move on the same line, for the same reason the code does: somebody
  // standing at the television configured a screen that was about to be claimed, and the
  // claim they were preparing for must not be what forgets it.
  rekeyScreenSettings(deviceId);
};

export function usePendingScreen(
  pollMs: number,
  /** The device this tab already IS. Announces an adopt code (see the header) instead of
   *  an ambient one. */
  adopt?: string,
  /** What this tab is PLAYING, for an ambient identity. Redeeming the code inherits it, so
   *  claiming a screen does not change what is on it. */
  viewId?: string,
  /**
   * Ask for party state. OFF by default, and the default is the important half.
   *
   * This hook runs on EVERY page — the site bar wears an ambient claimable code
   * everywhere, which is the whole reason it was extracted — and a browsing page is not a
   * screen: it will never draw a badge and has no gear to open. Reading a television's
   * settings there would be storage the front door touches for no reason, which
   * `role-entry.mts` asserts against by name, and it would be right to.
   *
   * So the two screen surfaces ask, and nothing else does.
   */
  opts?: {
    party?: boolean;
    /** Where this screen tab came from, restated on the claim's navigate so the device it
     *  becomes still knows (see lib/screenOrigin). Read at claim time, never tracked. */
    origin?: ScreenOrigin;
  },
): {
  code: string | null;
  /** True while the server cannot be reached; the hook keeps retrying on its own. */
  error: boolean;
  /** This screen's party settings and the hold on it — null unless asked for. */
  party: PartyState | null;
} {
  const navigate = useNavigate();
  const [code, setCode] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const viewIdRef = useRef(viewId);
  viewIdRef.current = viewId;
  const originRef = useRef(opts?.origin);
  originRef.current = opts?.origin;

  /*
   * PARTY STATE, keyed to the same identity everything else here is keyed to.
   *
   * It lives in this hook rather than beside it because the two moments that matter are
   * already here: the ANNOUNCE, which is how `holdMs` reaches the server, and the MINT,
   * which is where an ambient identity becomes a device and the settings have to travel
   * with it. A sibling module would have to agree with `keyOf()` about both, and the way
   * two modules agree about a key is that eventually they do not.
   */
  const partyOn = opts?.party === true;
  const settingsKey = screenKey(adopt);
  const [settings, setSettings] = useState<ScreenSettings>(() =>
    partyOn ? readScreenSettings(settingsKey) : { ...DEFAULT_SCREEN_SETTINGS },
  );
  useEffect(() => {
    if (partyOn) setSettings(readScreenSettings(settingsKey));
  }, [partyOn, settingsKey]);
  // The hold rides the announce, so it goes in a REF for the same reason `viewId` does:
  // putting it in the effect's dependencies would tear down the poll and re-announce every
  // time somebody moved the switch — a new code on the glass, for a change nobody watching
  // the television made.
  const holdRef = useRef(settings.holdMs);
  holdRef.current = settings.holdMs;
  /** This tab's own handle, so a screen with no device yet can still set its hold. */
  const [screenId, setScreenId] = useState<string | null>(null);
  /** A LOCAL deadline, converted from the duration the server reports. See useCountdown. */
  const [heldUntil, setHeldUntil] = useState<number | null>(null);

  const run = useCallback(
    (live: { on: boolean }) => {
      const schedule = (fn: () => void) => {
        if (live.on) timer.current = setTimeout(fn, pollMs);
      };
      const poll = (screenId: string) => {
        void api.screens
          .poll(screenId, viewIdRef.current)
          .then((r) => {
            if (!live.on) return;
            setHeldUntil(r.heldForMs ? Date.now() + r.heldForMs : null);
            if (r.deviceId && !adopt) {
              // Ambient entries only: an adopt entry's redemption happens in place and
              // the server never reports a device on it — this tab already IS one. Stated
              // as a condition rather than relied on, because the two branches do opposite
              // things and only one of them is survivable on a claimed screen.
              rekeyToDevice(r.deviceId, r.heldForMs ? Date.now() + r.heldForMs : null);
              navigate(`/view/d/${r.deviceId}`, { replace: true, state: originRef.current });
              return;
            }
            const cached = identities.get(keyOf(adopt));
            if (cached?.screenId === screenId) {
              cached.code = r.code;
              cached.heldUntil = r.heldForMs ? Date.now() + r.heldForMs : null;
              persist();
            }
            setCode(r.code);
            schedule(() => poll(screenId));
          })
          .catch((err: unknown) => {
            if (!live.on) return;
            // A 404 is the one answer that means the entry is GONE: ask for a fresh code
            // rather than sitting on a dead one, because a tab left open overnight must
            // still be claimable.
            //
            // Anything else — a dropped connection, a server blip, a deploy restart — is
            // not an answer ABOUT the entry, and rotating the four characters on the glass
            // over one is the rotation-under-the-reader this path exists to avoid. Hold
            // the code and ask again.
            const status =
              err instanceof Error ? Number(/^(\d{3})/.exec(err.message)?.[1]) : undefined;
            if (status !== 404) {
              schedule(() => poll(screenId));
              return;
            }
            forget(adopt);
            begin();
          });
      };
      const begin = () => {
        void announceOnce(adopt, viewIdRef.current, holdRef.current)
          .then((id) => {
            if (!live.on) return;
            setScreenId(id.screenId);
            setCode(id.code);
            setHeldUntil(id.heldUntil);
            setError(false);
            schedule(() => poll(id.screenId));
          })
          .catch(() => {
            if (!live.on) return;
            setError(true);
            schedule(begin);
          });
      };
      begin();
    },
    // NOT keyed on the played view: it changes when somebody swaps what a waiting screen
    // shows, and re-running this would tear down the poll and re-announce — a new code,
    // for a change nobody watching the television made. The ref is how the current value
    // reaches the next poll without re-arming anything.
    [navigate, pollMs, adopt],
  );

  useEffect(() => {
    const live = { on: true };
    run(live);
    return () => {
      live.on = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [run]);

  /** Where this screen's hold is filed: its device once it has one, its own handle until. */
  const holdTarget = adopt ?? screenId;

  const onChange = useCallback(
    (next: ScreenSettings) => {
      setSettings(next);
      writeScreenSettings(settingsKey, next);
      // `partyQr` stops here — whether a badge is drawn is nobody's business but this
      // browser's. Only the hold has to reach the server, because claims land there.
      if (holdTarget) {
        void api.screens
          .hold(holdTarget, { holdMs: next.holdMs })
          .then((r) => setHeldUntil(r.heldForMs ? Date.now() + r.heldForMs : null))
          .catch(() => {
            /* The next poll re-reads the truth; a dropped switch is not worth a dialog. */
          });
      }
    },
    [holdTarget, settingsKey],
  );

  const onRelease = useCallback(() => {
    // Cleared here as well as on the server so the badge and the dialog answer instantly.
    // The poll confirms it a beat later; if the request failed, the poll puts it back.
    setHeldUntil(null);
    if (!holdTarget) return;
    void api.screens.hold(holdTarget, { release: true }).catch(() => undefined);
  }, [holdTarget]);

  return {
    code,
    error,
    party: !partyOn
      ? null
      : {
          settings,
          onChange,
          // The origin this screen is actually being served from, never a hardcoded host:
          // a television on a LAN is at an IP and a port, and a QR pointing at
          // goonlords.com would send the phone to a different installation whose codes
          // are not these.
          url: code ? `${window.location.origin}/claim/${code}` : null,
          heldUntil,
          onRelease,
        },
  };
}
